import clsx from 'clsx'
import { Container, Icon } from 'gu-ui-kit'
import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { formatByLocale } from 'utils/formatMoney'
import { useBreakpoint } from 'utils/hoc/breakpointProvider'
import styles from 'modules/Statistics/Layout/Table/statistics-table.module.scss'
import CellStyles from 'modules/Statistics/Campaign/cells.module.scss'

export default function useColumnConfig(
  includeVat,
  tableData,
  summary,
  network = '',
) {
  const { sm: isMobile } = useBreakpoint()
  const { t } = useTranslation(['statistics'])
  const { i18n } = useSelector(({ app }) => app)
  let commonColumns
  const sticky = isMobile ? false : 'left'

  return useMemo(() => {
    switch (network) {
      case 'vk':
        return [
          {
            id: 'ad.id',
            Header: t('table.columns.header.ad'),
            accessor: 'ad.id',
            sticky,
            minWidth: 255,
            hideable: false,
            // eslint-disable-next-line react/prop-types
            Cell: ({ value }) => (
              <Container
                display="flex"
                className={styles['login-cell']}
                data-content={value}
              >
                <div className={'overflow'}>
                  <span className={styles['login']}>{value}</span>
                </div>
              </Container>
            ),
          },
          {
            id: 'ad.status',
            Header: t('table.columns.header.status'),
            accessor: 'ad.status',
            width: 130,
            hideable: false,
            Cell: (cellProps) => {
              switch (cellProps.value) {
                case 'active':
                  return (
                    <Icon
                      name={'status-on'}
                      size={'s'}
                      color={'secondary-10'}
                    />
                  )
                case 'archived':
                  return (
                    <Icon
                      name={'status-archive'}
                      size={'s'}
                      color={'basic-secondary-grey'}
                    />
                  )
                case 'paused':
                  return (
                    <Icon
                      name={'status-pause'}
                      size={'s'}
                      color={'basic-primary-grey'}
                    />
                  )
                default:
                  return '—'
              }
            },
            Footer: () => <>{t('table.footer.summary')}:</>,
          },
          {
            id: 'impressions',
            Header: t('table.columns.header.impressions'),
            accessor: 'impressions',
            minWidth: 120,
            width: 120,
            Cell: (cellProps) =>
              cellProps.value === null ? '—' : formatByLocale(cellProps.value),
            Footer: () =>
              summary?.impressions === null
                ? '—'
                : formatByLocale(summary?.impressions),
          },
          {
            id: 'cost',
            Header: t(
              'table.columns.header.' + (includeVat ? 'cost_with_vat' : 'cost'),
            ),
            accessor: includeVat ? 'cost_with_vat' : 'cost',
            minWidth: 150,
            Cell: (cellProps) =>
              cellProps.value === null
                ? '—'
                : formatByLocale(cellProps.value, i18n, {
                    maximumFractionDigits: 2,
                  }),
            Footer: () =>
              summary?.[includeVat ? 'cost_with_vat' : 'cost'] === null
                ? '—'
                : formatByLocale(
                    summary?.[includeVat ? 'cost_with_vat' : 'cost'],
                    i18n,
                    { maximumFractionDigits: 2 },
                  ),
          },
          {
            id: 'clicks',
            Header: t('table.columns.header.clicks'),
            accessor: 'clicks',
            minWidth: 100,
            width: 120,
            Cell: (cellProps) =>
              cellProps.value === null ? '—' : formatByLocale(cellProps.value),
            Footer: () =>
              summary?.clicks === null ? '—' : formatByLocale(summary?.clicks),
          },
          {
            id: 'ctr',
            Header: t('table.columns.header.ctr'),
            accessor: 'ctr',
            minWidth: 80,
            width: 100,
            Cell: (cellProps) =>
              cellProps.value === null
                ? '—'
                : formatByLocale(cellProps.value, i18n, {
                    maximumFractionDigits: 2,
                  }),
            Footer: () =>
              summary?.ctr === null
                ? '—'
                : formatByLocale(summary?.ctr, i18n, {
                    maximumFractionDigits: 2,
                  }),
          },
          {
            id: 'avg_click_price',
            Header: t(
              'table.columns.header.' +
                (includeVat ? 'avg_click_price_with_vat' : 'avg_click_price'),
            ),
            accessor: includeVat
              ? 'avg_click_price_with_vat'
              : 'avg_click_price',
            minWidth: 160,
            Cell: (cellProps) =>
              cellProps.value === null
                ? '—'
                : formatByLocale(cellProps.value, i18n, {
                    maximumFractionDigits: 2,
                  }),
            Footer: () =>
              summary?.[
                includeVat ? 'avg_click_price_with_vat' : 'avg_click_price'
              ] === null
                ? '—'
                : formatByLocale(
                    summary?.[
                      includeVat
                        ? 'avg_click_price_with_vat'
                        : 'avg_click_price'
                    ],
                    i18n,
                    { maximumFractionDigits: 2 },
                  ),
          },
        ]

      case 'googleads':
      case 'yandexdirect':
        commonColumns = [
          {
            id: 'noname',
            Header: '  ',
            sticky,
            Footer: () => <span></span>,
            columns: [
              {
                id: 'ad.id',
                Header: t('table.columns.header.ad'),
                accessor: 'ad.id',
                className: 'ad',
                minWidth: 190,
                hideable: false,
                Cell: ({
                  // eslint-disable-next-line react/prop-types
                  value,
                }) => (
                  <Container
                    display="flex"
                    className={styles['login-cell']}
                    data-content={value}
                  >
                    <div className={'overflow'}>
                      <span className={styles['login']}>{value}</span>
                    </div>
                  </Container>
                ),
                Footer: () => <>{t('table.footer.summary')}:</>,
              },
            ],
          },
          {
            id: 'common',
            Header: ' ',
            canSort: false,
            className: 'common',
            columns: [
              {
                id: 'ad.state',
                Header: t('table.columns.header.state'),
                accessor: 'ad.state',
                width: 130,
                hideable: false,
                Cell: (cellProps) => {
                  //ON, OFF, ARCHIVED, SUSPENDED, OFF_BY_MONITORING,
                  switch (cellProps.value?.toLowerCase()) {
                    case 'on':
                      return (
                        <Icon
                          name={'status-on'}
                          size={'s'}
                          color={'secondary-10'}
                        />
                      )
                    case 'off':
                      return (
                        <Icon
                          name={'status-blocked'}
                          size="s"
                          color={'text-error'}
                        />
                      )
                    case 'archived':
                      return (
                        <Icon
                          name={'status-archive'}
                          size={'s'}
                          color={'basic-secondary-grey'}
                        />
                      )
                    case 'suspended':
                      return (
                        <Icon
                          name={'status-finished'}
                          size={'s'}
                          color={'secondary-13'}
                        />
                      )
                    case 'off_by_monitoring':
                      return (
                        <Icon
                          name={'status-blocked'}
                          size="s"
                          color={'text-error'}
                        />
                      )
                    default:
                      return '—'
                  }
                },
                Footer: () => <></>,
              },
              {
                id: 'impressions',
                Header: t('table.columns.header.impressions'),
                accessor: 'impressions',
                minWidth: 120,
                width: 120,
                Cell: (cellProps) =>
                  cellProps.value === null
                    ? '—'
                    : formatByLocale(cellProps.value),
                Footer: () =>
                  summary?.impressions === null
                    ? '—'
                    : formatByLocale(summary?.impressions),
              },
              {
                id: 'cost',
                Header: t(
                  'table.columns.header.' +
                    (includeVat ? 'cost_with_vat' : 'cost'),
                ),
                accessor: includeVat ? 'cost_with_vat' : 'cost',
                minWidth: 150,
                Cell: (cellProps) =>
                  cellProps.value === null
                    ? '—'
                    : formatByLocale(cellProps.value, i18n, {
                        maximumFractionDigits: 2,
                      }),
                Footer: () =>
                  summary?.[includeVat ? 'cost_with_vat' : 'cost'] === null
                    ? '—'
                    : formatByLocale(
                        summary?.[includeVat ? 'cost_with_vat' : 'cost'],
                        i18n,
                        { maximumFractionDigits: 2 },
                      ),
              },
              {
                id: 'revenue',
                Header: t('table.columns.header.revenue_by_target'),
                accessor: 'revenue',
                minWidth: 100,
                width: 140,
                Cell: (cellProps) =>
                  cellProps.value === null
                    ? '—'
                    : formatByLocale(cellProps.value),
                Footer: () =>
                  summary?.revenue === null
                    ? '—'
                    : formatByLocale(summary?.revenue),
              },
              {
                id: 'clicks',
                Header: t('table.columns.header.clicks'),
                accessor: 'clicks',
                minWidth: 80,
                width: 100,
                Cell: (cellProps) =>
                  cellProps.value === null
                    ? '—'
                    : formatByLocale(cellProps.value),
                Footer: () =>
                  summary?.clicks === null
                    ? '—'
                    : formatByLocale(summary?.clicks),
              },
              {
                id: 'ctr',
                Header: t('table.columns.header.ctr'),
                accessor: 'ctr',
                minWidth: 70,
                width: 70,
                Cell: (cellProps) =>
                  cellProps.value === null
                    ? '—'
                    : formatByLocale(cellProps.value, i18n, {
                        maximumFractionDigits: 2,
                      }),
                Footer: () =>
                  summary?.ctr === null
                    ? '—'
                    : formatByLocale(summary?.ctr, i18n, {
                        maximumFractionDigits: 2,
                      }),
              },
              {
                id: 'avg_click_price',
                Header: t(
                  'table.columns.header.' +
                    (includeVat
                      ? 'avg_click_price_with_vat'
                      : 'avg_click_price'),
                ),
                accessor: includeVat
                  ? 'avg_click_price_with_vat'
                  : 'avg_click_price',
                minWidth: 165,
                Cell: (cellProps) =>
                  cellProps.value === null
                    ? '—'
                    : formatByLocale(cellProps.value, i18n, {
                        maximumFractionDigits: 2,
                      }),
                Footer: () =>
                  summary?.[
                    includeVat ? 'avg_click_price_with_vat' : 'avg_click_price'
                  ] === null
                    ? '—'
                    : formatByLocale(
                        summary?.[
                          includeVat
                            ? 'avg_click_price_with_vat'
                            : 'avg_click_price'
                        ],
                        i18n,
                        { maximumFractionDigits: 2 },
                      ),
              },
              {
                id: 'conversions',
                Header: t('table.columns.header.conversions'),
                accessor: 'conversions',
                minWidth: 120,
                width: 120,
                Cell: (cellProps) =>
                  cellProps.value === null
                    ? '—'
                    : formatByLocale(cellProps.value),
                Footer: () =>
                  summary?.conversions === null
                    ? '—'
                    : formatByLocale(summary?.conversions),
              },
              {
                id: 'conversion_rate',
                Header: t('table.columns.header.conversion_rate'),
                accessor: 'conversion_rate',
                minWidth: 100,
                width: 120,
                Cell: (cellProps) =>
                  cellProps.value === null
                    ? '—'
                    : formatByLocale(cellProps.value, i18n, {
                        maximumFractionDigits: 2,
                      }),
                Footer: () =>
                  summary?.conversion_rate === null
                    ? '—'
                    : formatByLocale(summary?.conversion_rate, i18n, {
                        maximumFractionDigits: 2,
                      }),
              },
            ],
            Footer: () => <span></span>,
          },
        ]

        return summary?.google_analytics_metrics &&
          summary?.google_analytics_metrics
          ? commonColumns.concat({
              Header: (
                <span className={CellStyles['header-cell__super']}>
                  Google Analytics
                </span>
              ),
              Footer: () => <span></span>,
              canSort: false,
              id: 'google_analytics',
              className: clsx(
                'grouped-cell',
                CellStyles['google_analytics_cell'],
              ),
              columns: [
                'bounce_rate',
                'bounces',
                'drr',
                // "goal_completions",
                // "goal_completions_all",
                // "goal_starts",
                // "goal_starts_all",
                // "goal_value_all",
                // "goal_values",
                'new_users',
                'pageviews',

                'roi',
                'session_duration',
                'sessions',
                'transaction_revenue',
                'transactions',
                'users',
              ].map((columnName) => ({
                id: 'google_analytics_metrics.' + columnName,
                Header: t('table.columns.ga_header.' + columnName),
                minWidth: 100,
                width: 150,
                accessor: 'google_analytics_metrics.' + columnName,
                className: clsx(
                  'grouped-cell',
                  CellStyles['google_analytics_cell'],
                ),
                Cell: (cellProps) =>
                  !cellProps.value
                    ? '—'
                    : formatByLocale(cellProps.value, i18n, {
                        maximumFractionDigits: 2,
                      }),
                Footer: () =>
                  summary?.google_analytics_metrics?.[columnName] === null
                    ? '—'
                    : formatByLocale(
                        summary?.google_analytics_metrics?.[columnName],
                        i18n,
                        { maximumFractionDigits: 2 },
                      ),
              })),
            })
          : commonColumns
    }
  }, [includeVat, i18n, tableData, network])
}
